/**
 * EvidenceCard — displays visual evidence (screenshots), WCAG criterion
 * context, and location details for an issue group.
 *
 * Screenshots are plan-gated. WCAG references are always shown when available.
 * When no screenshot exists for an issue, no placeholder or fake claim is shown.
 */

import { useState } from "react";
import type { ReportScreenshotRef } from "../api/product-types.js";
import { Link } from "react-router-dom";

interface EvidenceCardProps {
  screenshot: ReportScreenshotRef | null;
  criterion: string | null;
  criterionTitle: string | null;
  criterionWhyItMatters: string | null;
  wcagLevel: string | null;
  w3cUrl: string | null;
  screenshotsEnabled: boolean;
}

export function EvidenceCard({
  screenshot,
  criterion,
  criterionTitle,
  criterionWhyItMatters,
  wcagLevel,
  w3cUrl,
  screenshotsEnabled,
}: EvidenceCardProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const hasScreenshotImages =
    screenshot != null &&
    (screenshot.focusedUrl != null || screenshot.contextUrl != null);
  const hasLocation =
    screenshot != null &&
    (screenshot.pageUrl != null || screenshot.selector != null);
  const hasCriterion = criterion != null;

  // Nothing to display at all — skip rendering entirely
  if (!hasScreenshotImages && !hasLocation && !hasCriterion) return null;

  return (
    <>
      <div className="evidence-card">
        {/* ── Location details (page URL + selector) ───────────────── */}
        {hasLocation && (
          <div className="evidence-card__location">
            {screenshot.pageUrl && (
              <span className="evidence-card__page-url">
                <strong>Page:</strong>{" "}
                <a
                  href={screenshot.pageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {screenshot.pageUrl}
                </a>
              </span>
            )}
            {screenshot.selector && (
              <span className="evidence-card__selector">
                <strong>Selector:</strong> <code>{screenshot.selector}</code>
              </span>
            )}
          </div>
        )}

        {/* ── Screenshot section (plan-gated) ──────────────────────── */}
        {hasScreenshotImages && (
          <div className="evidence-card__screenshots">
            {screenshotsEnabled ? (
              <div className="evidence-card__images">
                {screenshot.focusedUrl && (
                  <button
                    type="button"
                    className="evidence-card__img-btn"
                    onClick={() =>
                      setLightboxSrc(`/api${screenshot.focusedUrl}`)
                    }
                    aria-label="View focused screenshot full size"
                  >
                    <img
                      src={`/api${screenshot.focusedUrl}`}
                      alt="Focused element screenshot"
                      className="evidence-card__img"
                      loading="lazy"
                    />
                    <span className="evidence-card__img-label">Element</span>
                  </button>
                )}
                {screenshot.contextUrl && (
                  <button
                    type="button"
                    className="evidence-card__img-btn"
                    onClick={() =>
                      setLightboxSrc(`/api${screenshot.contextUrl}`)
                    }
                    aria-label="View context screenshot full size"
                  >
                    <img
                      src={`/api${screenshot.contextUrl}`}
                      alt="Page context screenshot"
                      className="evidence-card__img"
                      loading="lazy"
                    />
                    <span className="evidence-card__img-label">Context</span>
                  </button>
                )}
              </div>
            ) : (
              <p className="evidence-card__upgrade">
                Visual evidence is available on paid plans.{" "}
                <Link to="/billing">View plans</Link>
              </p>
            )}
          </div>
        )}

        {/* ── WCAG criterion section (always shown when available) ── */}
        {hasCriterion && (
          <div className="evidence-card__wcag">
            <span className="evidence-card__criterion">
              <strong>WCAG {criterion}</strong>
              {criterionTitle && <> — {criterionTitle}</>}
              {wcagLevel && (
                <span className="evidence-card__level-badge">
                  Level {wcagLevel}
                </span>
              )}
            </span>
            {criterionWhyItMatters && (
              <p className="evidence-card__why">{criterionWhyItMatters}</p>
            )}
            {w3cUrl && (
              <a
                href={w3cUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="evidence-card__w3c-link"
              >
                W3C Understanding document
              </a>
            )}
          </div>
        )}
      </div>

      {/* ── Lightbox overlay ───────────────────────────────────────── */}
      {lightboxSrc && (
        <div
          className="evidence-lightbox"
          role="dialog"
          aria-label="Screenshot preview"
          aria-modal="true"
          tabIndex={-1}
          ref={(el) => el?.focus()}
          onClick={() => setLightboxSrc(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setLightboxSrc(null);
          }}
        >
          <button
            type="button"
            className="evidence-lightbox__close"
            onClick={() => setLightboxSrc(null)}
            aria-label="Close preview"
          >
            &times;
          </button>
          <img
            src={lightboxSrc}
            alt="Full-size screenshot"
            className="evidence-lightbox__img"
          />
        </div>
      )}
    </>
  );
}
